import { reactive } from 'vue'
import { apiGet } from './request'
import { REFRESH_INTERVAL_MS } from '@/utils/env'

/** 聚合端点失败时后端回退到最后一份快照：stale=true + capturedAt（不伪装实时）。 */
export interface SnapshotMeta {
  stale?: boolean
  capturedAt?: string
}

export interface MonthlyCommission {
  month: string
  amount: number
}

export interface TopAgent {
  agentId: number
  name: string
  commission: number
}

export interface CarrierMixItem {
  companyCode: string
  policies: number
  premium: number
}

export interface StateMixItem {
  state: string
  policies: number
}

/** S1 经营总览 */
export interface DashboardOverview extends SnapshotMeta {
  agents: number
  activePolicies: number
  policies: number
  clients: number
  annualPremium: number
  monthlyCommissionTrend: MonthlyCommission[]
  topAgents: TopAgent[]
  carrierMix: CarrierMixItem[]
  stateMix: StateMixItem[]
}

/** S2 佣金与对账 */
export interface CommissionApproved {
  cashTotal: number
  accrualTotal: number
  deferredTotal: number
  rowCount: number
  dataAsOf: string
}

export interface CommissionReconciliation {
  month: string
  diffAmount: number
  diffExplained: boolean
  carrierImportedAmount: number
  calculatedAmount: number
  netAmount: number
}

export interface CommissionTp {
  historicalTp: number
  finTp: number
  coefficient: number
  ruleVersion: string
  label: string
}

export interface CommissionReversal {
  agentId: number
  agentName: string
  amount: number
  month: string
}

export interface ThreeLayerMonth {
  month: string
  static: number
  approved: number | null
}

export interface CommissionDashboard extends SnapshotMeta {
  historicalStatic: number
  approved: CommissionApproved
  reconciliation: CommissionReconciliation
  tp: CommissionTp
  reversals: CommissionReversal[]
  threeLayerMonthly: ThreeLayerMonth[]
}

/** S3 团队与晋升 */
export interface TeamLeader {
  agentId: number
  name: string
  memberCount: number
}

export interface TpBucket {
  bucket: string
  count: number
}

export interface PromotionInfo {
  notEvaluable: number
  evaluable: number
  avgProgress: number | null
}

export interface TeamActivity {
  newPolicies30d: number
  activePolicies: number
  renewalsDue30d: number
}

export interface TeamDashboard extends SnapshotMeta {
  agents: number
  licensedAgents: number
  teamTree: TeamLeader[]
  tpBuckets: TpBucket[]
  promotion: PromotionInfo
  activity: TeamActivity
}

/** S5 AI 使用 */
export interface AiDailyPoint {
  date: string
  count: number
}

export interface AiTopItem {
  label: string
  count: number
}

export interface AiUsageDashboard extends SnapshotMeta {
  /** false = 后端缺依赖表（note 写清缺哪张）——前端显示「未接入」，不显示 0 */
  available: boolean
  sessions30d: number
  messages30d: number
  daily: AiDailyPoint[]
  topIntents: AiTopItem[]
  /** 兜底/拒答率：无落表来源，恒为 null */
  fallbackRate: number | null
  lastActivityAt: string | null
  note: string
}

/** S4 数据接入健康 */
export interface DataOpsDelivery {
  deliveryId: string
  sequence: number
  receivedAt: string
  rows: number
  hashOk: boolean
}

export interface DataOpsSync {
  sourceHealth: string
  remoteSourceHealth: string
  lastSuccessAt: string
  lastErrorCode: string
}

export interface DataOpsQualification {
  asOf: string
  validUntil: string
  isStale: boolean
  recordCount: number
}

export interface DataOpsFamily {
  canonicalNo: string
  policyNos: number
}

export interface DataOpsFamilies {
  total: number
  topFamilies: DataOpsFamily[]
}

/**
 * 用户反馈计数（`app_feedback`，migration 351）。
 * 字段全可选：后端未上线该字段时卡片走「暂无反馈数据」，不显示伪 0。
 */
export interface FeedbackCounts {
  /** 后端缺表/查询失败时为 false —— 前端据此显示「暂无反馈数据」 */
  available?: boolean
  new?: number
  triaged?: number
  confirmed?: number
  /** 后端契约字段名 snake_case；若后端走 camelCase 序列化则为 needsInfo（两种都读） */
  needs_info?: number
  needsInfo?: number
  duplicate?: number
  rejected?: number
  fixed?: number
  verified?: number
  closed?: number
  total?: number
}

export interface DataOpsDashboard extends SnapshotMeta {
  deliveries: DataOpsDelivery[]
  sync: DataOpsSync
  qualification: DataOpsQualification
  families: DataOpsFamilies
  feedback?: FeedbackCounts
  alerts: string[]
}

export interface DashboardState<T> {
  data: T | null
  lastSuccessAt: number
  error: string
  loading: boolean
  /** HTTP 成功但数据来自后端快照（stale=true） */
  stale: boolean
  capturedAt: string
}

/**
 * 通用轮询器：面板只读 state，避免每个面板各拉一次接口。
 * 失败时保留上一次成功数据，只把 error 抬出来给顶部黄条。
 */
function createPoller<T extends SnapshotMeta>(path: string) {
  const state = reactive({
    data: null as T | null,
    lastSuccessAt: 0,
    error: '',
    loading: false,
    stale: false,
    capturedAt: '',
  }) as DashboardState<T>

  let timer: number | undefined

  async function refresh(): Promise<void> {
    state.loading = true
    try {
      const data = await apiGet<T>(path)
      state.data = data
      state.stale = !!data.stale
      state.capturedAt = data.capturedAt || ''
      if (!data.stale) state.lastSuccessAt = Date.now()
      state.error = ''
    } catch (err) {
      state.error = (err as Error).message || '聚合接口不可用'
    } finally {
      state.loading = false
    }
  }

  /** 立即拉一次 + 周期刷新；返回取消函数。 */
  function start(intervalMs: number = REFRESH_INTERVAL_MS): () => void {
    refresh()
    timer = window.setInterval(refresh, intervalMs)
    return () => {
      if (timer !== undefined) window.clearInterval(timer)
      timer = undefined
    }
  }

  return { state, refresh, start }
}

/** S1 经营总览 */
export const overviewPoller = createPoller<DashboardOverview>('/dashboard/overview')
export const dashboardState = overviewPoller.state
export const refreshOverview = overviewPoller.refresh
export const startOverviewPolling = overviewPoller.start

/** S2 佣金与对账 */
export const commissionPoller = createPoller<CommissionDashboard>('/dashboard/commission')
export const commissionState = commissionPoller.state
export const startCommissionPolling = commissionPoller.start

/** S4 数据接入健康 */
export const dataOpsPoller = createPoller<DataOpsDashboard>('/dashboard/data-ops')
export const dataOpsState = dataOpsPoller.state
export const startDataOpsPolling = dataOpsPoller.start

/** S3 团队与晋升 */
export const teamPoller = createPoller<TeamDashboard>('/dashboard/team')
export const teamState = teamPoller.state
export const startTeamPolling = teamPoller.start

/** S5 AI 使用 */
export const aiPoller = createPoller<AiUsageDashboard>('/dashboard/ai')
export const aiUsageState = aiPoller.state
export const startAiPolling = aiPoller.start
